import React from 'react';
import { Sparkles, AlertTriangle, Target, ShieldCheck, Clock, Wallet } from 'lucide-react';
import { formatSAR, timeAgo } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * SmartSummary — top-of-Debtor-360 "what should this agent do right now?" card.
 * Answers: who · what · why prioritised · last event · next step · what to avoid.
 */
export const SmartSummary = ({ debtor: d }) => {
  if (!d) return null;
  const exposureBucket = d.amount > 50000 ? 'High value' : d.amount > 15000 ? 'Mid value' : 'Low value';
  const riskTone = d.scores.legalRisk > 65 ? 'destructive' : d.scores.legalRisk > 40 ? 'warning' : 'success';
  const nbA = d.nextBestAction || 'Call 18:00–20:00, then WhatsApp payment-plan link';

  const reasons = [];
  if (d.scores.priority >= 80) reasons.push('Priority ≥ 80');
  if (d.delinquencyDays > 120) reasons.push(`${d.delinquencyDays}-day delinquency`);
  if (d.amount > 50000) reasons.push('Exposure > SAR 50K');
  if (d.scores.propensity >= 70) reasons.push('High payment propensity');
  if (d.holds?.dispute) reasons.push('Dispute hold');
  if (d.holds?.hardship) reasons.push('Hardship review');
  if (d.holds?.legal) reasons.push('Legal pipeline');

  const avoid = [];
  if (d.consent?.whatsapp === false) avoid.push('WhatsApp (opted out)');
  if (d.contactStats?.call30d >= 8) avoid.push(`Call cap near SAMA limit (${d.contactStats.call30d}/10)`);
  if (d.holds?.dispute) avoid.push('Any outreach while dispute open');
  if (d.holds?.hardship) avoid.push('Pressure tactics during hardship review');

  return (
    <div className="surface-card p-5 border-l-4 border-l-primary">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-primary/15 text-gold-deep flex items-center justify-center shrink-0">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="flex-1 space-y-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-primary">Smart case summary</p>
              <h3 className="text-base font-semibold mt-0.5">
                {d.name} · {d.product} · {formatSAR(d.amount)} owed
              </h3>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant="outline" className="text-[10px]">{exposureBucket}</Badge>
              <Badge variant="outline" className={cn(
                'text-[10px]',
                riskTone === 'destructive' && 'bg-destructive/10 text-destructive border-destructive/30',
                riskTone === 'warning' && 'bg-warning/10 text-warning border-warning/40',
                riskTone === 'success' && 'bg-success/10 text-success border-success/40',
              )}>Legal risk {d.scores.legalRisk}</Badge>
              <Badge variant="outline" className="text-[10px]">Propensity {d.scores.propensity}</Badge>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-3 text-[12px]">
            <SummaryTile
              icon={Target}
              label="Why prioritised"
              body={reasons.slice(0, 3).join(' · ') || 'Standard backlog item'}
            />
            <SummaryTile
              icon={Clock}
              label="Last event"
              body={d.contactStats?.lastContactDaysAgo
                ? `${d.contactStats.lastContactDaysAgo}d ago · ${d.assignedAgentName}`
                : 'No recent contact'}
            />
            <SummaryTile
              icon={Sparkles}
              label="Next best action"
              body={nbA}
              tone="primary"
            />
            <SummaryTile
              icon={AlertTriangle}
              label="What to avoid"
              body={avoid.length ? avoid.slice(0, 2).join(' · ') : 'No specific cautions'}
              tone={avoid.length ? 'warning' : 'muted'}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const SummaryTile = ({ icon: Icon, label, body, tone = 'muted' }) => (
  <div className={cn(
    'rounded-lg border border-border bg-muted/40 p-3',
    tone === 'primary' && 'border-primary/40 bg-primary/5',
    tone === 'warning' && 'border-warning/40 bg-warning/5',
  )}>
    <div className="flex items-center gap-1.5 mb-1">
      <Icon className={cn(
        'h-3 w-3',
        tone === 'primary' ? 'text-primary' : tone === 'warning' ? 'text-warning' : 'text-muted-foreground'
      )} />
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
    </div>
    <p className="text-[12px] leading-snug">{body}</p>
  </div>
);

export default SmartSummary;
